import React from 'react';

interface Column<T> {
  key: string;
  header: React.ReactNode;
  render?: (item: T) => React.ReactNode;
  className?: string;
}

interface TableProps<T> {
  columns: Column<T>[];
  data: T[];
  onRowClick?: (item: T) => void;
  expandedRow?: (item: T) => React.ReactNode | null;
  emptyMessage?: string;
}

// Helper — does this column carry a meaningful label we can show on a mobile card?
// Empty / icon-only / select / action columns are rendered without a label.
const labelFor = (header: React.ReactNode, key: string): string | null => {
  if (key === '_select' || key === '_actions' || key === 'actions') return null;
  if (typeof header === 'string') {
    const t = header.trim();
    if (!t) return null;
    return t;
  }
  return null;
};

export function Table<T extends { id: number | string; uuid?: string }>({ columns, data, onRowClick, expandedRow, emptyMessage = 'No data found' }: TableProps<T>) {
  return (
    <>
      {/* ── Desktop table (md+) ─────────────────────────────────────────── */}
      <div className="intrlock-table-wrap hidden md:block overflow-x-auto">
        <table className="intrlock-table w-full">
          <thead>
            <tr className="border-b border-gray-200">
              {columns.map((col) => (
                <th key={col.key} className={`text-left text-sm font-bold text-gray-700 uppercase tracking-wider py-4 px-4 ${col.className || ''}`}>
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-center py-12 text-gray-400">{emptyMessage}</td></tr>
            ) : (
              data.map((item) => {
                const expandedContent = expandedRow ? expandedRow(item) : null;
                return (
                  <React.Fragment key={item.id}>
                    <tr onClick={() => onRowClick?.(item)} className={`${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''} transition-colors`}>
                      {columns.map((col) => (
                        <td key={col.key} className={`py-3 px-4 text-sm text-gray-700 ${col.className || ''}`}>
                          {col.render ? col.render(item) : (item as any)[col.key]}
                        </td>
                      ))}
                    </tr>
                    {expandedContent && (
                      <tr>
                        <td colSpan={columns.length} className="p-0">
                          {expandedContent}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Mobile cards (<md) ──────────────────────────────────────────────
          Auto-generated from the same columns: first labeled column becomes
          the card title, the rest render as label / value rows. Columns
          without a label (selection checkbox, action buttons) collapse into
          a small footer row so they remain clickable.                       */}
      <div className="md:hidden p-3 space-y-2">
        {data.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-6 text-center text-sm text-gray-400">
            {emptyMessage}
          </div>
        ) : (
          data.map((item) => {
            const expandedContent = expandedRow ? expandedRow(item) : null;

            // Find the first column with a real label — that becomes the card title.
            let titleColIndex = -1;
            for (let i = 0; i < columns.length; i++) {
              if (labelFor(columns[i].header, columns[i].key)) { titleColIndex = i; break; }
            }
            const titleCol = titleColIndex >= 0 ? columns[titleColIndex] : null;

            return (
              <React.Fragment key={item.id}>
                <div
                  className={`bg-white rounded-xl border border-gray-200 p-3 ${onRowClick ? 'cursor-pointer active:bg-gray-50' : ''}`}
                  onClick={() => onRowClick?.(item)}
                >
                  {/* Title row */}
                  {titleCol && (
                    <div className="text-base font-semibold text-gray-900 mb-1.5">
                      {titleCol.render ? titleCol.render(item) : (item as any)[titleCol.key]}
                    </div>
                  )}

                  {/* Detail rows */}
                  <div className="space-y-1 text-xs">
                    {columns.map((col, idx) => {
                      if (idx === titleColIndex) return null;
                      const lbl = labelFor(col.header, col.key);
                      if (!lbl) return null;
                      const val = col.render ? col.render(item) : (item as any)[col.key];
                      return (
                        <div key={col.key} className="flex items-baseline gap-2">
                          <span className="text-gray-400 uppercase tracking-wider text-[10px] font-medium min-w-[80px] flex-shrink-0">{lbl}</span>
                          <span className="text-gray-700 min-w-0 flex-1 break-words">{val}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Footer row for actions / unlabeled columns */}
                  {(() => {
                    const footerCols = columns.filter((c, idx) => idx !== titleColIndex && !labelFor(c.header, c.key));
                    if (footerCols.length === 0) return null;
                    return (
                      <div
                        className="flex items-center gap-3 pt-2 mt-2 border-t border-gray-100"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {footerCols.map((col) => (
                          <span key={col.key}>
                            {col.render ? col.render(item) : (item as any)[col.key]}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* Expanded panel — same content as the desktop expanded row */}
                {expandedContent && (
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    {expandedContent}
                  </div>
                )}
              </React.Fragment>
            );
          })
        )}
      </div>
    </>
  );
}
